import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get, get, has, some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  claimFnolQuery,
  fromClaimFnolActions,
  ReportClaimFnolService
} from '@amfam/claim/fnol/data-access';
import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
import { FnolRiskModel } from '@amfam/policy/models';
import { AnalyticsActions } from '@amfam/shared/analytics';
import { ClaimsNavigationPaths } from '@amfam/shared/models';
import { QuickLink } from '@amfam/shared/ui/quick-links';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { DockingBarService, LoadingSpinnerService, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';

@Component({
  selector: 'ds-report-claim-fnol',
  templateUrl: './report-claim-fnol.component.html',
  styleUrls: ['./report-claim-fnol.component.scss'],
  standalone: false
})
export class ReportClaimFnolComponent implements OnInit, OnDestroy {
  claimsEnterpriseApp = false;
  isContactBillingToggleEnabled = this.feature.isEnabled('contact_billing');

  private stop$: Subject<void> = new Subject<void>();
  public quickLinksList = new Array<QuickLink>();

  private draftClaimNumber: string;

  public loading: Observable<boolean>;

  // Array of risks used to resume draft claim
  public risks: Array<FnolRiskModel> = new Array<FnolRiskModel>();

  constructor(
    private route: ActivatedRoute,
    private spinner: LoadingSpinnerService,
    private store: Store<fromRoot.RootState>,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private reportClaimFnolService: ReportClaimFnolService,
    private feature: FeatureFlagService
  ) {}

  ngOnInit() {
    this.claimsEnterpriseApp = this.feature.isEnabled('claims_enterprise_app');
    this.pageTitle.set('File a Claim');
    this.dockingBarService.registerHeading('File a Claim');
    if (this.claimsEnterpriseApp) {
      setTimeout(() => {
        this.store.dispatch(
          AnalyticsActions.sendAnalytic({
            options: {
              button: {
                link: 'MyAccount:Claims:FileAClaim'
              }
            }
          })
        );
      });
      this.store.dispatch(
        fromRouterActions.Go({
          path: [ClaimsNavigationPaths.CLAIM_FNOL_FILE_CLAIM_PATH]
        })
      );
      // don't want to run rest of code
      return;
    }
    this.store
      .select(BrandSelectors.selectIsPartner)
      .pipe(takeUntil(this.stop$))
      .subscribe(isPartner => {
        this.quickLinksList = [
          new QuickLink('Make a Payment', '/billing'),
          new QuickLink('Request a Change', '/policies/changerequest'),
          new QuickLink('Contact a Billing Specialist', '/billing/contact'),
          new QuickLink('FAQs', env.links.amfamDotComClaimsFaqUrl, 'amfam')
        ]
          .filter(link => !(get(link, 'text') === 'FAQs' && isPartner))
          .filter(
            link =>
              // fitler Contact a Billing Specialist link if contact billing is toggled off
              !(
                _get(link, 'text') === 'Contact a Billing Specialist' &&
                !this.isContactBillingToggleEnabled
              )
          );
      });
    // Grab the claim number from the route parameter
    this.route.params.pipe(takeUntil(this.stop$)).subscribe(params => {
      this.draftClaimNumber = params['draftClaimNumber'];
      if (this.draftClaimNumber) {
        this.draftClaimNumber = this.draftClaimNumber.replace(/-/g, '');
        this.resumeFnol();
      }
    });
  }

  ngOnDestroy() {
    this.dockingBarService.clear();
    this.stop$.next();
    this.stop$.complete();
  }

  private resumeFnol() {
    this.store.dispatch(new fromClaimFnolActions.ResumeDraftClaim(this.draftClaimNumber));

    // Top-level loading spinner for claims
    this.loading = this.store.select(claimFnolQuery.getClaimFnolAnyLoading);

    // Turn on / off the spinner based on loading of an individual fnol detail object
    this.loading.pipe(takeUntil(this.stop$)).subscribe(loading => {
      if (loading) {
        this.spinner.start({
          blockActions: true
        });
      } else {
        this.spinner.stop();
      }
    });

    // Get the list of claims from the store.
    combineLatest(
      this.store.select(claimFnolQuery.getClaimFnols),
      this.store.select(PolicySelectors.selectClaimableRiskModels),
      this.loading,
      (fnols, risks, loading) => ({
        fnols: fnols,
        risks: risks,
        loading: loading
      })
    )
      .pipe(
        filter(state => state && !some(state.fnols, { loading: true })),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        // TODO: What to do if the load detail fnol call fails? Throw generic error (top-level app error);

        if (state.loading) {
          return;
        }

        this.risks = state.risks;
        const matchingFnol = state.fnols.find(
          fnol => get(fnol, 'claimNumber') === this.draftClaimNumber
        );

        // Determine if we have the detail object loaded
        if (get(matchingFnol, 'detailsLoaded') === true && has(matchingFnol, 'fnolDetail')) {
          // Make sure the risk listed on the draft claim is still available
          if (!this.checkVinValidity(matchingFnol)) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [ClaimsNavigationPaths.CLAIM_CALLUS_CONTACT_PATH]
              })
            );
            return;
          }

          // Make sure there are no injurys in the draft claim before resuming
          if (String(get(matchingFnol.fnolDetail, 'injuryIndicator')) === 'true') {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [ClaimsNavigationPaths.CLAIM_CALLUS_INJURY_PATH]
              })
            );
            return;
          }

          this.reportClaimFnolService.getRoutingPath(matchingFnol);
        } else {
          // Route to the contact us page - this fnol is not in the correct shape
          this.store.dispatch(
            fromRouterActions.Go({
              path: [ClaimsNavigationPaths.CLAIM_CALLUS_CONTACT_PATH]
            })
          );
        }
      });
  }

  private checkVinValidity(draftClaim): boolean {
    let valid = false;

    if (
      has(draftClaim, 'autoClaimDetail.vehicles') &&
      draftClaim.autoClaimDetail.vehicles.length > 0 &&
      draftClaim.autoClaimDetail.vehicles[0].vin
    ) {
      const matchingVin = draftClaim.autoClaimDetail.vehicles[0].vin;
      if (this.risks) {
        valid = some(this.risks, { vin: matchingVin });
      }
    }

    return valid;
  }
}
